import React from 'react';
import { Link } from 'react-router-dom';
import { Shield, Award, CheckCircle, Clock, Users, Star, Phone } from 'lucide-react';

const ABOUT_HERO = "https://images.unsplash.com/photo-1504307651254-35680f356dfd?w=1400&q=85";
const TEAM_IMG = "https://images.unsplash.com/photo-1751054571128-30d45eccbe42?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85";
const HOUSE_IMG = "https://images.unsplash.com/photo-1600585154340-be6161a56a0c?w=900&q=85";

const values = [
  { icon: Award, title: "Quality Craftsmanship", desc: "We never compromise on materials or workmanship. Every finish is held to the highest standard." },
  { icon: CheckCircle, title: "Transparent Communication", desc: "Clear, honest updates from initial quote through to project completion — no surprises." },
  { icon: Users, title: "Client-Focused Service", desc: "Your vision guides every decision. We listen, adapt, and deliver what you actually want." },
  { icon: Shield, title: "Code-Compliant Work", desc: "All work meets or exceeds Ontario building codes. We handle permits and inspections in-house." },
  { icon: Clock, title: "On-Time Delivery", desc: "We respect your time and stick to agreed timelines, keeping your project on schedule." },
  { icon: Star, title: "Full-Service Warranty", desc: "We stand behind our work with a comprehensive post-renovation warranty on every project." },
];

const serviceAreas = [
  "Toronto", "Hamilton", "Barrie", "Kitchener", "Mississauga", "Oakville",
  "Burlington", "Guelph", "Brampton", "Cambridge", "Waterloo", "London",
  "Windsor", "St. Catharines", "Markham", "Vaughan", "Oshawa", "Ajax",
];

export default function About() {
  return (
    <main data-testid="about-page">

      {/* PAGE HERO */}
      <section
        className="relative h-72 md:h-96 pt-20 flex items-end"
        style={{ backgroundImage: `url(${ABOUT_HERO})`, backgroundSize: 'cover', backgroundPosition: 'center' }}
      >
        <div className="absolute inset-0 bg-slate-950/70" />
        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 pb-12 w-full">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#00A2E8] mb-2">WHO WE ARE</p>
          <h1 className="text-4xl md:text-5xl font-black uppercase tracking-tighter text-white leading-none">
            About Ariana<br />General Contracting
          </h1>
        </div>
      </section>

      {/* STORY */}
      <section className="py-20 md:py-32 bg-white" data-testid="about-story">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#00A2E8] mb-3">OUR STORY</p>
              <h2 className="text-3xl md:text-4xl font-extrabold tracking-tight text-slate-900 mb-6">
                Building Ontario, One Project at a Time
              </h2>
              <p className="text-base text-slate-600 leading-relaxed mb-4">
                Ariana General Contracting was founded with a single mission: to deliver exceptional building and finishing services with integrity. Over 25 years of experience, we have built our reputation on quality craftsmanship, transparent communication, and results that exceed expectations.
              </p>
              <p className="text-base text-slate-600 leading-relaxed mb-4">
                We serve homeowners and businesses across Ontario — from Toronto to Barrie, Hamilton to Kitchener. Every project is fully managed in-house, from initial consultation through permits, construction, and final walkthrough.
              </p>
              <p className="text-base text-slate-600 leading-relaxed mb-8">
                We are licensed, insured, and committed to code-compliant construction. Every project is managed with precision from consultation through to final walkthrough, ensuring your vision is realized with quality that lasts.
              </p>
              <Link
                to="/contact"
                data-testid="about-get-quote-btn"
                className="inline-block bg-[#00A2E8] hover:bg-[#0088C4] text-white px-8 py-4 font-bold text-sm tracking-widest transition-colors duration-200"
              >
                START YOUR PROJECT
              </Link>
            </div>
            <div className="relative">
              <img
                src={HOUSE_IMG}
                alt="Quality home construction"
                className="w-full h-[480px] object-cover"
              />
              <div className="absolute -bottom-6 -left-6 bg-[#00A2E8] text-white p-6 hidden md:block">
                <p className="text-4xl font-black leading-none">11+</p>
                <p className="text-xs font-semibold tracking-wide mt-1 uppercase opacity-90">Services<br />Offered</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* VALUES */}
      <section className="py-20 md:py-32 bg-slate-50" data-testid="values-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-14">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#00A2E8] mb-3">WHAT WE STAND FOR</p>
            <h2 className="text-3xl md:text-4xl font-extrabold tracking-tight text-slate-900">Our Core Values</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {values.map((v, i) => {
              const Icon = v.icon;
              return (
                <div key={i} data-testid={`value-card-${i}`} className="bg-white p-8 border border-slate-200 hover:border-[#00A2E8] hover:shadow-[0_10px_40px_-10px_rgba(0,162,232,0.15)] transition-all duration-300 group">
                  <div className="w-12 h-12 bg-[#00A2E8]/10 flex items-center justify-center mb-4 group-hover:bg-[#00A2E8] transition-colors duration-300">
                    <Icon className="w-6 h-6 text-[#00A2E8] group-hover:text-white transition-colors duration-300" strokeWidth={1.5} />
                  </div>
                  <h3 className="text-base font-bold text-slate-900 mb-2">{v.title}</h3>
                  <p className="text-sm text-slate-500 leading-relaxed">{v.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* TEAM */}
      <section className="py-20 md:py-32 bg-white" data-testid="team-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#00A2E8] mb-3">LEADERSHIP</p>
              <h2 className="text-3xl md:text-4xl font-extrabold tracking-tight text-slate-900 mb-6">
                Meet Jay Wahid
              </h2>
              <p className="text-base text-slate-600 leading-relaxed mb-4">
                Jay Wahid is the founder and lead contractor at Ariana General Contracting. With over 25 years of hands-on experience in residential and commercial construction across Ontario, Jay brings a commitment to precision, quality, and client satisfaction to every project.
              </p>
              <p className="text-base text-slate-600 leading-relaxed mb-8">
                Jay personally oversees each project to ensure it meets Ariana's high standards. From the initial consultation to the final walkthrough, he is your dedicated point of contact throughout the project journey.
              </p>
              <div className="flex flex-col gap-3 text-sm">
                <div className="flex items-center gap-3 text-slate-700">
                  <Phone className="w-4 h-4 text-[#00A2E8] shrink-0" strokeWidth={1.5} />
                  <a href="tel:+14165703834" className="hover:text-[#00A2E8] transition-colors font-semibold">+1 416 570 3834</a>
                </div>
                <div className="flex items-center gap-3 text-slate-700">
                  <span className="w-4 h-4 text-[#00A2E8] shrink-0 text-xs font-bold">@</span>
                  <a href="mailto:Jay@ArianaBuild.ca" className="hover:text-[#00A2E8] transition-colors font-semibold">Jay@ArianaBuild.ca</a>
                </div>
              </div>
            </div>
            <div>
              <img
                src={TEAM_IMG}
                alt="Jay Wahid - Ariana General Contracting"
                className="w-full h-[500px] object-cover object-top"
              />
            </div>
          </div>
        </div>
      </section>

      {/* SERVICE AREAS */}
      <section className="py-20 md:py-28 bg-slate-950" data-testid="service-areas">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-12">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#00A2E8] mb-3">WHERE WE WORK</p>
            <h2 className="text-3xl md:text-4xl font-extrabold tracking-tight text-white">
              Serving Ontario
            </h2>
            <p className="text-slate-400 text-base mt-4 max-w-xl mx-auto">
              We serve homeowners and businesses across Ontario. If your city isn't listed, contact us — we may still be able to help.
            </p>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-3">
            {serviceAreas.map(city => (
              <div key={city} className="border border-slate-700 px-3 py-2 text-center text-sm text-slate-400 hover:border-[#00A2E8] hover:text-[#00A2E8] transition-all duration-200">
                {city}
              </div>
            ))}
          </div>
          <div className="text-center mt-10">
            <Link to="/contact" data-testid="about-contact-btn" className="inline-block bg-[#00A2E8] hover:bg-[#0088C4] text-white px-8 py-4 font-bold text-sm tracking-widest transition-colors duration-200">
              CONTACT US TODAY
            </Link>
          </div>
        </div>
      </section>

    </main>
  );
}
